import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import {
  getApiTable,
  isApiSuccess,
} from "../components/Pages/qms/qmsApiUtils";

function firstNonEmptyTable(response, ...tableNames) {
  for (const name of tableNames) {
    const rows = getApiTable(response, name);
    if (Array.isArray(rows) && rows.length) return rows;
  }
  return [];
}

function extractRows(response) {
  const direct = firstNonEmptyTable(
    response,
    "SuccessStory",
    "Table",
    "table",
  );
  if (direct.length) return direct;

  const payload = response?.data ?? response?.Data ?? null;
  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === "object") {
    // Prefer FAQ-shaped tables (have Question/FaqId), not Modules.
    const tableValues = Object.values(payload).filter(Array.isArray);
    const faqShaped = tableValues.find(
      (rows) =>
        rows.length &&
        rows[0] &&
        (rows[0].Question != null ||
          rows[0].question != null ||
          rows[0].FaqId != null ||
          rows[0].faqId != null),
    );
    if (faqShaped) return faqShaped;
    if (tableValues.length === 1) return tableValues[0];
  }

  return [];
}

function mapFaqRow(row) {
  return {
    faqId: String(row.FaqId || row.faqId || "").trim(),
    mainModule: String(row.MainModule || row.mainModule || "").trim(),
    subModule: String(row.SubModule || row.subModule || "").trim(),
    question: String(row.Question || row.question || "").trim(),
    answer: String(row.Answer || row.answer || "").trim(),
    videoLink: String(row.VideoLink || row.videoLink || "").trim(),
    sortOrder: Number(row.SortOrder ?? row.sortOrder ?? 0),
  };
}

function minFaqSortOrder(faqs) {
  if (!faqs?.length) return Number.MAX_SAFE_INTEGER;
  return Math.min(
    ...faqs.map((f) => Number(f.sortOrder) || Number.MAX_SAFE_INTEGER),
  );
}

/**
 * Group flat API rows into FAQ.jsx nested shape: { Main: { Sub: [faqs] } }.
 * Main modules and sub-modules are ordered by the minimum FAQ SortOrder in
 * that group (so Manage drag-reorder persists for public FAQ too).
 */
export function groupFaqsNested(rows) {
  const nested = {};
  (rows || []).forEach((row) => {
    const main = row.mainModule || "General";
    const sub = row.subModule || "General";
    if (!nested[main]) nested[main] = {};
    if (!nested[main][sub]) nested[main][sub] = [];
    nested[main][sub].push({
      faqId: row.faqId,
      question: row.question,
      answer: row.answer,
      videoLink: row.videoLink,
      sortOrder: row.sortOrder,
      mainModule: main,
      subModule: sub,
    });
  });

  Object.keys(nested).forEach((main) => {
    const subs = nested[main];
    Object.keys(subs).forEach((sub) => {
      subs[sub].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    });

    const orderedSubs = Object.keys(subs).sort((a, b) => {
      const minA = minFaqSortOrder(subs[a]);
      const minB = minFaqSortOrder(subs[b]);
      if (minA !== minB) return minA - minB;
      return a.localeCompare(b);
    });

    const reordered = {};
    orderedSubs.forEach((sub) => {
      reordered[sub] = subs[sub];
    });
    nested[main] = reordered;
  });

  // Order main modules by min SortOrder across all their FAQs.
  const orderedMains = Object.keys(nested).sort((a, b) => {
    const allA = Object.values(nested[a] || {}).flat();
    const allB = Object.values(nested[b] || {}).flat();
    const minA = minFaqSortOrder(allA);
    const minB = minFaqSortOrder(allB);
    if (minA !== minB) return minA - minB;
    return a.localeCompare(b);
  });

  const orderedNested = {};
  orderedMains.forEach((main) => {
    orderedNested[main] = nested[main];
  });
  return orderedNested;
}

/**
 * Flatten faqs for one main module in sub order, renumber SortOrder 1..n.
 * Prefer renumberAllFaqs when main-module order must be preserved globally.
 */
export function renumberFaqsInMain(subOrder, faqsBySub) {
  let next = 1;
  const items = [];
  (subOrder || []).forEach((sub) => {
    (faqsBySub[sub] || []).forEach((faq) => {
      items.push({
        faqId: faq.faqId,
        mainModule: faq.mainModule || "",
        subModule: sub || faq.subModule || "",
        question: faq.question || "",
        answer: faq.answer || "",
        videoLink: faq.videoLink || "",
        sortOrder: next,
      });
      next += 1;
    });
  });
  return items;
}

/**
 * Flatten all FAQs across mains → subs → questions with one contiguous
 * SortOrder sequence so main / sub / question drag order all persist.
 * mainOrder: string[]; nested: { [main]: { [sub]: faq[] } }.
 */
export function renumberAllFaqs(mainOrder, nested) {
  let next = 1;
  const items = [];
  (mainOrder || []).forEach((main) => {
    const subs = nested?.[main] || {};
    Object.keys(subs).forEach((sub) => {
      (subs[sub] || []).forEach((faq) => {
        items.push({
          faqId: faq.faqId,
          mainModule: main || faq.mainModule || "",
          subModule: sub || faq.subModule || "",
          question: faq.question || "",
          answer: faq.answer || "",
          videoLink: faq.videoLink || "",
          sortOrder: next,
        });
        next += 1;
      });
    });
  });
  return items;
}

/** Deep-clone nested FAQ map so reorder helpers can mutate safely. */
export function cloneFaqNested(nested) {
  const copy = {};
  Object.keys(nested || {}).forEach((main) => {
    copy[main] = {};
    Object.keys(nested[main] || {}).forEach((sub) => {
      copy[main][sub] = [...(nested[main][sub] || [])];
    });
  });
  return copy;
}

function extractNamedRows(response, tableName) {
  const named = getApiTable(response, tableName);
  if (Array.isArray(named) && named.length) return named;

  const payload = response?.data ?? response?.Data ?? null;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const matchKey = Object.keys(payload).find(
      (key) => key.toLowerCase() === String(tableName).toLowerCase(),
    );
    if (matchKey && Array.isArray(payload[matchKey])) return payload[matchKey];
  }
  return Array.isArray(named) ? named : [];
}

/** Master modules from Mst_Org_Modules (Modules / Table1 / ModuleName-shaped rows). */
function extractModuleRows(response) {
  const named = [
    ...extractNamedRows(response, "Modules"),
    ...extractNamedRows(response, "Table1"),
  ];
  if (named.length && named.some((r) => r?.ModuleName || r?.moduleName)) {
    return named.filter((r) => r?.ModuleName || r?.moduleName);
  }

  const payload = response?.data ?? response?.Data ?? null;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return [];
  }

  for (const value of Object.values(payload)) {
    if (!Array.isArray(value) || !value.length) continue;
    const sample = value[0];
    if (!sample || typeof sample !== "object") continue;
    const hasModule =
      sample.ModuleName != null || sample.moduleName != null;
    const looksLikeFaq =
      sample.FaqId != null ||
      sample.faqId != null ||
      sample.Question != null ||
      sample.question != null;
    if (hasModule && !looksLikeFaq) return value;
  }

  return [];
}

/**
 * Load active platform FAQs (all authenticated users).
 * Returns { rows, nested, currentDatabaseName, modules }.
 * `modules` comes from dbo.Mst_Org_Modules (distinct ModuleName).
 */
export async function fetchPlatformFaqs() {
  const response = await api.post(API_ENDPOINTS.FAQ.GET_PLATFORM_FAQS, {
    ActionType: "GetAll",
  });

  const raw = extractRows(response);
  if (!isApiSuccess(response) && raw.length === 0) {
    const msg = response?.message || response?.Message || "";
    if (msg) throw new Error(msg);
  }

  const rows = raw.map(mapFaqRow).filter((r) => r.question || r.answer);
  const currentDatabaseName = String(
    raw[0]?.CurrentDatabaseName ||
      raw[0]?.currentDatabaseName ||
      response?.message ||
      "",
  ).trim();

  const moduleRows = extractModuleRows(response);
  const modules = [];
  const seen = new Set();
  moduleRows
    .map((row) => ({
      name: String(row.ModuleName || row.moduleName || "").trim(),
      index: Number(row.IndexNumber ?? row.indexNumber ?? 0),
    }))
    .filter((row) => row.name)
    .sort((a, b) => a.index - b.index || a.name.localeCompare(b.name))
    .forEach((row) => {
      const key = row.name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      modules.push(row.name);
    });

  return {
    rows,
    nested: groupFaqsNested(rows),
    currentDatabaseName,
    modules,
  };
}

/** Super Admin — create or update one FAQ on the current DB. */
export async function savePlatformFaq({
  faqId,
  mainModule,
  subModule,
  question,
  answer,
  videoLink,
  sortOrder,
  staffId,
  actionType,
}) {
  const normalizedAction =
    actionType === "Update"
      ? "Update"
      : actionType === "Reorder"
        ? "Reorder"
        : "Add";

  const response = await api.post(API_ENDPOINTS.FAQ.SAVE_PLATFORM_FAQ, {
    ActionType: normalizedAction,
    FaqId: faqId || "",
    MainModule: mainModule || "",
    SubModule: subModule || "",
    Question: question || "",
    Answer: answer || "",
    VideoLink: videoLink || "",
    SortOrder: Number(sortOrder) > 0 ? Number(sortOrder) : 0,
    StaffId: staffId || "",
  });

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not save FAQ.");
  }

  const row = extractRows(response)[0] || {};
  return {
    message: response?.message || "Saved.",
    faqId: String(row.FaqId || row.faqId || faqId || "").trim(),
  };
}

/**
 * Super Admin — persist display order in one API/SP call (ReorderBulk).
 * Only FaqId + SortOrder are sent — no Answer HTML — so train stays smooth.
 */
export async function reorderPlatformFaqs({ staffId, items, previousItems }) {
  const list = (items || []).filter(
    (item) => item?.faqId && Number(item.sortOrder) > 0,
  );
  if (!list.length) return { message: "Nothing to reorder." };
  if (!staffId) {
    throw new Error("Super Admin StaffId not found in session.");
  }

  // Skip rows whose SortOrder did not change (fewer DB writes).
  const prevMap = new Map(
    (previousItems || []).map((item) => [
      String(item.faqId || ""),
      Number(item.sortOrder) || 0,
    ]),
  );
  const changed = list.filter((item) => {
    const prev = prevMap.get(String(item.faqId));
    return prev == null || prev !== Number(item.sortOrder);
  });
  const payload = changed.length ? changed : list;

  const saveSequentially = async () => {
    for (const item of payload) {
      await savePlatformFaq({
        faqId: item.faqId,
        sortOrder: item.sortOrder,
        staffId,
        actionType: "Reorder",
      });
    }
    return { message: `Updated order for ${payload.length} FAQ(s).` };
  };

  try {
    const response = await api.post(API_ENDPOINTS.FAQ.SAVE_PLATFORM_FAQ, {
      ActionType: "ReorderBulk",
      StaffId: staffId || "",
      OrderItems: payload.map((item) => ({
        FaqId: item.faqId,
        SortOrder: Number(item.sortOrder),
      })),
    });

    if (!isApiSuccess(response)) {
      // Older API/SP without ReorderBulk — fall back to per-row Reorder.
      return saveSequentially();
    }

    return {
      message:
        response?.message || `Updated order for ${payload.length} FAQ(s).`,
    };
  } catch {
    return saveSequentially();
  }
}

/** Super Admin — hard-delete one FAQ on the current DB. */
export async function deletePlatformFaq({ faqId, staffId }) {
  const response = await api.post(API_ENDPOINTS.FAQ.DELETE_PLATFORM_FAQ, {
    ActionType: "Delete",
    FaqId: faqId || "",
    StaffId: staffId || "",
  });

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not delete FAQ.");
  }

  return { message: response?.message || "Deleted." };
}

/** Super Admin — hard-delete all FAQs on the current DB. */
export async function deleteAllPlatformFaqs({ staffId }) {
  const response = await api.post(API_ENDPOINTS.FAQ.DELETE_ALL_PLATFORM_FAQS, {
    ActionType: "DeleteAll",
    StaffId: staffId || "",
  });

  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not delete all FAQs.");
  }

  return { message: response?.message || "All FAQs deleted." };
}

/**
 * Super Admin — import FAQ rows (upsert by Main module + Sub module + Question).
 * items: [{ mainModule, subModule, question, answer, videoLink? }]
 */
export async function importPlatformFaqs({ staffId, items }) {
  const response = await api.post(API_ENDPOINTS.FAQ.IMPORT_PLATFORM_FAQS, {
    StaffId: staffId || "",
    Items: (items || []).map((item) => ({
      MainModule: item.mainModule || "",
      SubModule: item.subModule || "",
      Question: item.question || "",
      Answer: item.answer || "",
      VideoLink: item.videoLink || "",
      SortOrder: Number(item.sortOrder) > 0 ? Number(item.sortOrder) : 0,
    })),
  });

  const results = extractRows(response).map((row) => ({
    rowNumber: Number(row.RowNumber ?? row.rowNumber ?? 0),
    faqId: String(row.FaqId || row.faqId || "").trim(),
    success: Number(row.Success ?? row.success ?? 0) === 1,
    message: String(row.MSG || row.message || "").trim(),
  }));

  if (!isApiSuccess(response) && results.every((r) => !r.success)) {
    throw new Error(response?.message || "Import failed.");
  }

  return {
    message: response?.message || "Import complete.",
    results,
  };
}

/** List FAQ user guides (module name + public HTML/PDF link). */
export async function fetchPlatformUserGuides() {
  const response = await api.post(API_ENDPOINTS.FAQ.GET_USER_GUIDES, {});
  if (!isApiSuccess(response) && extractRows(response).length === 0) {
    throw new Error(response?.message || "Could not load user guides.");
  }
  const guides = extractRows(response)
    .map((row) => ({
      moduleKey: String(row.ModuleKey || row.moduleKey || "").trim(),
      moduleName: String(row.ModuleName || row.moduleName || "").trim(),
      fileName: String(row.FileName || row.fileName || "").trim(),
      fileUrl: String(row.FileUrl || row.fileUrl || "").trim(),
      fileSizeBytes: Number(row.FileSizeBytes ?? row.fileSizeBytes ?? 0),
      modifiedUtc: String(row.ModifiedUtc || row.modifiedUtc || "").trim(),
      sortOrder: Number(row.SortOrder ?? row.sortOrder ?? 0),
    }))
    .filter((g) => g.moduleKey)
    .sort((a, b) => {
      const sa = a.sortOrder > 0 ? a.sortOrder : Number.MAX_SAFE_INTEGER;
      const sb = b.sortOrder > 0 ? b.sortOrder : Number.MAX_SAFE_INTEGER;
      if (sa !== sb) return sa - sb;
      return (a.moduleName || a.moduleKey).localeCompare(
        b.moduleName || b.moduleKey,
      );
    });
  return { guides, message: response?.message || "" };
}

/** Super Admin — save/replace a module user guide (name + public link). */
export async function savePlatformUserGuide({
  staffId,
  moduleName,
  fileUrl,
  fileName,
  sortOrder,
}) {
  const response = await api.post(API_ENDPOINTS.FAQ.SAVE_USER_GUIDE, {
    StaffId: staffId || "",
    ModuleName: moduleName || "",
    FileName: fileName || "",
    FileUrl: fileUrl || "",
    SortOrder: Number(sortOrder) > 0 ? Number(sortOrder) : 0,
  });
  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not save user guide.");
  }
  const row = extractRows(response)[0] || {};
  return {
    message: response?.message || "Saved.",
    replaced: Number(row.Replaced ?? row.replaced ?? 0) === 1,
    moduleKey: String(row.ModuleKey || row.moduleKey || moduleName || "").trim(),
    fileUrl: String(row.FileUrl || row.fileUrl || fileUrl || "").trim(),
    sortOrder: Number(row.SortOrder ?? row.sortOrder ?? 0),
  };
}

/** Super Admin — delete a module user guide. */
export async function deletePlatformUserGuide({ staffId, moduleName }) {
  const response = await api.post(API_ENDPOINTS.FAQ.DELETE_USER_GUIDE, {
    StaffId: staffId || "",
    ModuleName: moduleName || "",
  });
  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not delete user guide.");
  }
  return { message: response?.message || "Deleted." };
}

/** Super Admin — persist user-guide SortOrder. */
export async function reorderPlatformUserGuides({ staffId, items }) {
  const response = await api.post(API_ENDPOINTS.FAQ.REORDER_USER_GUIDES, {
    StaffId: staffId || "",
    OrderItems: (items || []).map((item, index) => ({
      ModuleName: item.moduleKey || item.moduleName || "",
      SortOrder:
        Number(item.sortOrder) > 0 ? Number(item.sortOrder) : index + 1,
    })),
  });
  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not reorder user guides.");
  }
  return { message: response?.message || "Reordered." };
}

/**
 * Resolve a user-guide for preview.
 * Prefers the stored public URL; falls back to legacy base64 for old disk guides.
 */
export async function fetchPlatformUserGuideFile(moduleName) {
  const response = await api.post(API_ENDPOINTS.FAQ.GET_USER_GUIDE_FILE, {
    ModuleName: moduleName || "",
  });
  if (!isApiSuccess(response)) {
    throw new Error(response?.message || "Could not load user guide.");
  }
  const row = extractRows(response)[0] || {};
  return {
    moduleKey: String(row.ModuleKey || row.moduleKey || "").trim(),
    moduleName: String(row.ModuleName || row.moduleName || "").trim(),
    fileName: String(row.FileName || row.fileName || "guide.pdf").trim(),
    contentType: String(
      row.ContentType || row.contentType || "application/pdf",
    ).trim(),
    fileUrl: String(row.FileUrl || row.fileUrl || "").trim(),
    fileBase64: String(row.FileBase64 || row.fileBase64 || "").trim(),
    fileSizeBytes: Number(row.FileSizeBytes ?? row.fileSizeBytes ?? 0),
    sortOrder: Number(row.SortOrder ?? row.sortOrder ?? 0),
  };
}

export async function fetchFaqAiStatus(organizationId, staffId) {
  const response = await api.post(API_ENDPOINTS.FAQ.GET_FAQ_AI_STATUS, {
    OrganizationId: organizationId || "",
    StaffId: staffId || "",
  });
  const payload = response?.data ?? response?.Data ?? {};
  const modules = Array.isArray(payload.modules)
    ? payload.modules
    : Array.isArray(payload.Modules)
      ? payload.Modules
      : [];
  const num = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    enabled: isApiSuccess(response) && !!(payload.enabled ?? payload.Enabled),
    orgEnabled: !!(payload.orgEnabled ?? payload.OrgEnabled),
    formBuilderEnabled: !!(
      payload.formBuilderEnabled ?? payload.FormBuilderEnabled
    ),
    formBuilderOrgEnabled: !!(
      payload.formBuilderOrgEnabled ?? payload.FormBuilderOrgEnabled
    ),
    formBuilderModes: (() => {
      const raw =
        payload.formBuilderModes ?? payload.FormBuilderModes ?? [];
      if (Array.isArray(raw)) {
        return raw.map((m) => String(m || "").trim()).filter(Boolean);
      }
      return String(raw || "")
        .split(/[,;]/)
        .map((m) => m.trim())
        .filter(Boolean);
    })(),
    modules: modules.map((name) => String(name || "").trim()).filter(Boolean),
    message: response?.message || response?.Message || "",
    dailyOrgCap: num(payload.dailyOrgCap ?? payload.DailyOrgCap, 50),
    dailyUserCap: num(payload.dailyUserCap ?? payload.DailyUserCap, 50),
    maxQuestionLength: num(
      payload.maxQuestionLength ?? payload.MaxQuestionLength,
      1000,
    ),
    maxQuestionLineBreaks: num(
      payload.maxQuestionLineBreaks ?? payload.MaxQuestionLineBreaks,
      6,
    ),
    orgUsedToday: num(payload.orgUsedToday ?? payload.OrgUsedToday, 0),
    userUsedToday: num(payload.userUsedToday ?? payload.UserUsedToday, 0),
    orgRemaining: num(payload.orgRemaining ?? payload.OrgRemaining, 0),
    userRemaining: num(payload.userRemaining ?? payload.UserRemaining, 0),
  };
}

/** Normalise FAQ AI grounding sources (Ask + chat history). */
export function normalizeFaqAiSources(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((item) => ({
      faqId: String(item?.faqId ?? item?.FaqId ?? "").trim(),
      question: String(item?.question ?? item?.Question ?? "").trim(),
      mainModule: String(item?.mainModule ?? item?.MainModule ?? "").trim(),
      subModule: String(item?.subModule ?? item?.SubModule ?? "").trim(),
      videoLink: String(item?.videoLink ?? item?.VideoLink ?? "").trim(),
    }))
    .filter((item) => item.question || item.faqId)
    .slice(0, 6);
}

export const AQ_ASSIST_DISCLAIMER =
  "AQ Assist is AI-generated and may be inaccurate or incomplete. Check the FAQ or user guide before relying on it.";

export async function askPlatformFaq({
  organizationId,
  staffId,
  module,
  question,
  chatSessionId,
}) {
  const response = await api.post(API_ENDPOINTS.FAQ.ASK_PLATFORM_FAQ, {
    OrganizationId: organizationId || "",
    StaffId: staffId || "",
    Module: module || "",
    Question: question || "",
    ChatSessionId: chatSessionId || "",
  });
  if (!isApiSuccess(response)) {
    throw new Error(response?.message || response?.Message || "Could not get an answer.");
  }
  const payload = response?.data ?? response?.Data ?? {};
  const followUpsRaw = Array.isArray(payload.followUps)
    ? payload.followUps
    : Array.isArray(payload.FollowUps)
      ? payload.FollowUps
      : [];
  const sourcesRaw = Array.isArray(payload.sources)
    ? payload.sources
    : Array.isArray(payload.Sources)
      ? payload.Sources
      : [];
  return {
    answer: String(payload.answer ?? payload.Answer ?? "").trim(),
    tokens: Number(payload.tokens ?? payload.Tokens ?? 0),
    requestId: String(payload.requestId ?? payload.RequestId ?? "").trim(),
    followUps: followUpsRaw
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, 4),
    sources: normalizeFaqAiSources(sourcesRaw),
  };
}

/**
 * Form Builder AQ Assist — server quota + optional Azure structured edit.
 * When useLocal is true, run generateESourceFormFromPrompt on the client.
 */
export async function askFormBuilderAssist({
  organizationId,
  staffId,
  question,
  chatSessionId,
  formName,
  formHeader,
  canvasSnapshot,
  allowedModes,
}) {
  const response = await api.post(API_ENDPOINTS.FAQ.ASK_FORM_BUILDER_AI, {
    OrganizationId: organizationId || "",
    StaffId: staffId || "",
    Question: question || "",
    ChatSessionId: chatSessionId || "",
    FormName: formName || "",
    FormHeader: formHeader || "",
    CanvasSnapshot:
      typeof canvasSnapshot === "string"
        ? canvasSnapshot
        : JSON.stringify(canvasSnapshot || []),
    AllowedModes: Array.isArray(allowedModes)
      ? allowedModes.join(",")
      : String(allowedModes || ""),
  });
  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message ||
        response?.Message ||
        "Could not process Form Builder Assist.",
    );
  }
  const payload = response?.data ?? response?.Data ?? {};
  const num = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const fieldsRaw = payload.fields ?? payload.Fields ?? null;
  const operationsRaw = payload.operations ?? payload.Operations ?? null;
  return {
    requestId: String(payload.requestId ?? payload.RequestId ?? "").trim(),
    useLocal: !!(payload.useLocal ?? payload.UseLocal),
    engine: String(payload.engine ?? payload.Engine ?? "").trim(),
    summary: String(payload.summary ?? payload.Summary ?? "").trim(),
    blocked: !!(payload.blocked ?? payload.Blocked),
    replaceCanvas: payload.replaceCanvas ?? payload.ReplaceCanvas,
    incremental: !!(payload.incremental ?? payload.Incremental),
    name: String(payload.name ?? payload.Name ?? "").trim() || null,
    code: String(payload.code ?? payload.Code ?? "").trim() || null,
    moduleScope:
      String(payload.moduleScope ?? payload.ModuleScope ?? "").trim() || null,
    fields: Array.isArray(fieldsRaw) ? fieldsRaw : null,
    operations: Array.isArray(operationsRaw) ? operationsRaw : null,
    dailyOrgCap: num(payload.dailyOrgCap ?? payload.DailyOrgCap, 50),
    dailyUserCap: num(payload.dailyUserCap ?? payload.DailyUserCap, 50),
    orgUsedToday: num(payload.orgUsedToday ?? payload.OrgUsedToday, 0),
    userUsedToday: num(payload.userUsedToday ?? payload.UserUsedToday, 0),
    orgRemaining: num(payload.orgRemaining ?? payload.OrgRemaining, 0),
    userRemaining: num(payload.userRemaining ?? payload.UserRemaining, 0),
  };
}

export async function setFaqAiFeedback({
  organizationId,
  staffId,
  requestId,
  wasHelpful,
  feedbackReason,
}) {
  const reason = String(feedbackReason || "").trim().slice(0, 500);
  const response = await api.post(API_ENDPOINTS.FAQ.SET_FAQ_AI_FEEDBACK, {
    OrganizationId: organizationId || "",
    StaffId: staffId || "",
    RequestId: requestId || "",
    WasHelpful: !!wasHelpful,
    FeedbackReason: reason || undefined,
  });
  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || response?.Message || "Could not save feedback.",
    );
  }
  const payload = response?.data ?? response?.Data ?? {};
  return {
    requestId: String(
      payload.requestId ?? payload.RequestId ?? requestId ?? "",
    ).trim(),
    wasHelpful: !!(payload.wasHelpful ?? payload.WasHelpful ?? wasHelpful),
    feedbackReason: String(
      payload.feedbackReason ?? payload.FeedbackReason ?? reason ?? "",
    ).trim(),
  };
}

export async function fetchFaqAiChatHistory(organizationId, staffId, take = 20) {
  const response = await api.post(API_ENDPOINTS.FAQ.GET_FAQ_AI_CHAT_HISTORY, {
    OrganizationId: organizationId || "",
    StaffId: staffId || "",
    Take: take,
  });
  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || response?.Message || "Could not load chat history.",
    );
  }
  const payload = response?.data ?? response?.Data ?? {};
  const rows = Array.isArray(payload.sessions)
    ? payload.sessions
    : Array.isArray(payload.Sessions)
      ? payload.Sessions
      : [];
  return rows.map((row) => ({
    id: String(row.id || row.Id || "").trim(),
    module: String(row.module || row.Module || "").trim(),
    title: String(row.title || row.Title || "").trim(),
    updatedAt: Number(row.updatedAt ?? row.UpdatedAt ?? 0),
    hasHelpful: !!(row.hasHelpful ?? row.HasHelpful),
    hasNotHelpful: !!(row.hasNotHelpful ?? row.HasNotHelpful),
  }));
}

export async function fetchFaqAiChatSession(
  organizationId,
  staffId,
  chatSessionId,
) {
  const response = await api.post(API_ENDPOINTS.FAQ.GET_FAQ_AI_CHAT_SESSION, {
    OrganizationId: organizationId || "",
    StaffId: staffId || "",
    ChatSessionId: chatSessionId || "",
  });
  if (!isApiSuccess(response)) {
    throw new Error(
      response?.message || response?.Message || "Could not load this chat.",
    );
  }
  const payload = response?.data ?? response?.Data ?? {};
  const messagesRaw = Array.isArray(payload.messages)
    ? payload.messages
    : Array.isArray(payload.Messages)
      ? payload.Messages
      : [];
  return {
    id: String(payload.id || payload.Id || chatSessionId || "").trim(),
    module: String(payload.module || payload.Module || "").trim(),
    updatedAt: Number(payload.updatedAt ?? payload.UpdatedAt ?? 0),
    messages: messagesRaw.map((m) => ({
      role: String(m.role || m.Role || "").trim(),
      text: String(m.text || m.Text || "").trim(),
      tokens:
        typeof m.tokens === "number"
          ? m.tokens
          : typeof m.Tokens === "number"
            ? m.Tokens
            : undefined,
      requestId: String(m.requestId || m.RequestId || "").trim() || undefined,
      wasHelpful:
        typeof m.wasHelpful === "boolean"
          ? m.wasHelpful
          : typeof m.WasHelpful === "boolean"
            ? m.WasHelpful
            : m.wasHelpful == null && m.WasHelpful == null
              ? undefined
              : !!(m.wasHelpful ?? m.WasHelpful),
      sources: normalizeFaqAiSources(m.sources ?? m.Sources),
    })),
  };
}
